import React, { useState, useRef, useCallback } from 'react';
import { BookOpen, ExternalLink, RefreshCw, Home, Loader2, AlertCircle } from 'lucide-react';

/**
 * Renders content for WIKI type spaces
 * Config includes:
 * - base_url: Wiki service URL (required)
 * - default_path: Initial page path (optional, defaults to "/")
 * - show_header_bar: Show URL bar with navigation (default: true)
 */
const WikiSpace = ({ space, theme = 'dark' }) => {
  const { config, name } = space;
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build the full wiki URL
  const getWikiUrl = useCallback((path = '/') => {
    if (!config?.base_url) return null;
    const base = config.base_url.replace(/\/$/, ''); // Remove trailing slash
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${base}${cleanPath}`;
  }, [config?.base_url]);

  const wikiUrl = getWikiUrl(config?.default_path || '/');
  const showHeader = config?.show_header_bar ?? true;

  // Handle iframe load
  const handleLoad = () => {
    setLoading(false);
    setError(null);
  };

  // Handle iframe error
  const handleError = () => {
    setLoading(false);
    setError('Failed to load wiki. The service may be unavailable.');
  };

  // Refresh the iframe
  const handleRefresh = () => {
    if (iframeRef.current) {
      setLoading(true);
      setError(null);
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  // Navigate to home
  const handleHome = () => {
    if (iframeRef.current && wikiUrl) {
      setLoading(true);
      setError(null);
      iframeRef.current.src = wikiUrl;
    }
  };

  // No config - show placeholder
  if (!config?.base_url) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400">
        <BookOpen size={48} className="mb-4 opacity-50" />
        <h2 className="text-xl font-semibold mb-2">{name}</h2>
        <p className="text-sm">This wiki space is not configured yet.</p>
        <p className="text-xs mt-2 text-gray-500">
          Set the wiki URL in the Space Dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header Bar */}
      {showHeader && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-800/50 border-b border-gray-700/50">
          <div className="flex items-center gap-2 text-gray-400">
            <BookOpen size={16} />
            <span className="text-sm truncate max-w-md">{wikiUrl}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleHome}
              className="p-1.5 rounded hover:bg-gray-700/50 text-gray-400 hover:text-gray-200 transition-colors"
              title="Go to wiki home"
            >
              <Home size={16} />
            </button>
            <button
              onClick={handleRefresh}
              className="p-1.5 rounded hover:bg-gray-700/50 text-gray-400 hover:text-gray-200 transition-colors"
              title="Refresh wiki"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <a
              href={wikiUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-gray-700/50 text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
              title="Open in new tab"
            >
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 relative">
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 z-10">
            <div className="flex flex-col items-center text-gray-400">
              <Loader2 size={32} className="animate-spin mb-2" />
              <span className="text-sm">Loading wiki...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
            <div className="flex flex-col items-center text-red-400">
              <AlertCircle size={48} className="mb-4" />
              <p className="text-sm mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-gray-200 text-sm transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Wiki Iframe */}
        <iframe
          ref={iframeRef}
          src={wikiUrl}
          className="w-full h-full border-0"
          title={name}
          onLoad={handleLoad}
          onError={handleError}
          allow="pointer-lock; clipboard-read; clipboard-write"
          sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation allow-same-origin allow-pointer-lock"
        />
      </div>
    </div>
  );
};

export default WikiSpace;
